import rawApprovedImages from './approved-product-images.generated.json';
import {
  getCatalogLookupKey,
  getCatalogVariantVolumeMl,
  normalizeCatalogVariantLabel,
  parseCatalogProductText,
} from './catalog-name-utils';

type GeneratedApprovedImageRecord = {
  id: string;
  title: string;
  volumeMl: number | null;
  image: string;
};

export interface ApprovedImageVariantRef {
  label?: string | null;
  volumeMl?: number | null;
}

interface ApprovedImageRecord {
  id: string;
  image: string;
  lookupKey: string;
  variantLabel: string | null;
  volumeMl: number | null;
  order: number;
}

interface NormalizedVariantRef {
  label: string | null;
  volumeMl: number | null;
}

const APPROVED_PRODUCT_ASSET_PREFIX = '/assets/products/approved/';

const approvedImageRecords: ApprovedImageRecord[] = (rawApprovedImages as GeneratedApprovedImageRecord[])
  .map((entry, order) => {
    const parsed = parseCatalogProductText(entry.title);
    const variantLabel = parsed.variantLabel === 'REGULAR' ? null : parsed.variantLabel;

    return {
      id: entry.id,
      image: entry.image,
      lookupKey: getCatalogLookupKey(parsed.name),
      variantLabel,
      volumeMl: entry.volumeMl ?? parsed.volumeMl,
      order,
    };
  })
  .filter((entry) => Boolean(entry.lookupKey) && Boolean(entry.image));

function normalizeVariantRequests(
  productName: string,
  variants: ApprovedImageVariantRef[] | undefined
): { lookupKey: string; requests: NormalizedVariantRef[] } {
  const parsedProduct = parseCatalogProductText(productName);
  const requests: NormalizedVariantRef[] = [];

  if (parsedProduct.variantLabel !== 'REGULAR' || parsedProduct.volumeMl !== null) {
    requests.push({
      label: parsedProduct.variantLabel === 'REGULAR' ? null : parsedProduct.variantLabel,
      volumeMl: parsedProduct.volumeMl,
    });
  }

  for (const variant of variants ?? []) {
    const label = variant.label ? normalizeCatalogVariantLabel(variant.label) : null;
    const volumeMl = variant.volumeMl ?? getCatalogVariantVolumeMl(label);
    requests.push({
      label: label === 'REGULAR' ? null : label,
      volumeMl,
    });
  }

  const dedupedRequests = requests.filter(
    (request, index, collection) =>
      collection.findIndex(
        (entry) => entry.label === request.label && entry.volumeMl === request.volumeMl
      ) === index
  );

  return {
    lookupKey: getCatalogLookupKey(parsedProduct.name),
    requests: dedupedRequests,
  };
}

function isExactVariantMatch(record: ApprovedImageRecord, request: NormalizedVariantRef): boolean {
  if (request.label && record.variantLabel && request.label === record.variantLabel) {
    return true;
  }

  if (request.volumeMl !== null && record.volumeMl !== null && request.volumeMl === record.volumeMl) {
    return true;
  }

  return false;
}

function getVariantMatchIndex(
  record: ApprovedImageRecord,
  requests: NormalizedVariantRef[]
): number {
  const index = requests.findIndex((request) => isExactVariantMatch(record, request));
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}

function uniqueImages(images: string[]): string[] {
  return images.filter((image, index) => images.indexOf(image) === index);
}

export function isApprovedProductAsset(imageUrl: string | null | undefined): boolean {
  if (!imageUrl) {
    return false;
  }

  return imageUrl.startsWith(APPROVED_PRODUCT_ASSET_PREFIX);
}

export function getApprovedImagesForProduct(options: {
  productName: string;
  variants?: ApprovedImageVariantRef[];
  existingImages?: string[];
}): string[] {
  const { productName, variants, existingImages = [] } = options;
  const { lookupKey, requests } = normalizeVariantRequests(productName, variants);
  const candidates = approvedImageRecords.filter((record) => record.lookupKey === lookupKey);

  if (candidates.length === 0) {
    return uniqueImages(existingImages);
  }

  const hasExplicitVariantRequest = requests.length > 0;
  const matchedCandidates = hasExplicitVariantRequest
    ? candidates.filter((record) =>
        requests.some((request) => isExactVariantMatch(record, request))
      )
    : candidates;

  if (hasExplicitVariantRequest && matchedCandidates.length === 0) {
    return uniqueImages(existingImages);
  }

  const prioritizedCandidates = [...matchedCandidates].sort(
    (recordA, recordB) =>
      getVariantMatchIndex(recordA, requests) - getVariantMatchIndex(recordB, requests) ||
      (recordB.volumeMl ?? -1) - (recordA.volumeMl ?? -1) ||
      recordA.order - recordB.order
  );

  // Prefer explicitly stored product images (e.g. Supabase storage URLs/keys) first,
  // but fall back to the approved catalog assets when needed.
  return uniqueImages([
    ...existingImages,
    ...prioritizedCandidates.map((record) => record.image),
  ]);
}
